/**
 * Parse a Deribit instrument symbol into its base ticker and quote currency.
 *
 * Key format rules:
 *   - The "pair prefix" is everything before the FIRST hyphen (`-`)
 *     e.g. 'BTC', 'BTC_USDC', 'ETH'
 *   - `_` inside the pair prefix means a stablecoin-quoted pair (e.g. BTC_USDC)
 *   - `_` AFTER the first `-` is a calendar-spread separator — NOT a pair separator
 *   - `-` is always the contract specifier separator
 *
 * Examples:
 *   BTC-PERPETUAL                → { baseTicker: 'BTC',  quoteCurrency: 'USD'  }
 *   BTC_USDC-PERPETUAL           → { baseTicker: 'BTC',  quoteCurrency: 'USDC' }
 *   BTC-27FEB26                  → { baseTicker: 'BTC',  quoteCurrency: 'USD'  }
 *   BTC_USDC-27FEB26             → { baseTicker: 'BTC',  quoteCurrency: 'USDC' }
 *   BTC-FS-25SEP26_27FEB26       → { baseTicker: 'BTC',  quoteCurrency: 'USD'  }
 *   BTC-FS-25SEP26_PERP          → { baseTicker: 'BTC',  quoteCurrency: 'USD'  }
 *   BTC_USDC-FS-27FEB26_PERP     → { baseTicker: 'BTC',  quoteCurrency: 'USDC' }
 *   ETH-USD                      → { baseTicker: 'ETH',  quoteCurrency: 'USD'  }
 *   KRW-BTC (Upbit, QUOTE-BASE)  → { baseTicker: 'BTC',  quoteCurrency: 'KRW'  }
 */
// Upbit lists symbols as QUOTE-BASE (e.g. 'KRW-BTC' = BTC priced in KRW) — the
// reverse of the BASE[-QUOTE]-... convention this parser otherwise assumes.
// Only KRW is handled here: Deribit never lists a KRW-quoted instrument, so a
// 'KRW-' prefix is an unambiguous signal to flip base/quote. Upbit's BTC- and
// USDT-quoted markets (also QUOTE-BASE) can't be told apart from this string
// alone — those need real exchange context, not a string heuristic, and are
// out of scope here (see resolveBaseCurrency/resolveQuoteCurrency, which
// already prefer the actual Instrument record and only fall back to this
// parser when that lookup misses).
const UPBIT_QUOTE_PREFIXES = ['KRW']

export function parseInstrumentSymbol(symbol: string): {
  baseTicker: string
  quoteCurrency: string
} {
  // Pair prefix = everything before the first hyphen
  const hyphenIdx = symbol.indexOf('-')
  const prefix = hyphenIdx >= 0 ? symbol.slice(0, hyphenIdx) : symbol

  if (hyphenIdx >= 0 && UPBIT_QUOTE_PREFIXES.includes(prefix.toUpperCase())) {
    return {
      baseTicker: symbol.slice(hyphenIdx + 1).toUpperCase(),
      quoteCurrency: prefix.toUpperCase(),
    }
  }

  // Check if the prefix contains a stablecoin quote (e.g. BTC_USDC, ETH_USDT)
  const STABLECOINS = ['USDC', 'USDT']
  const underscoreIdx = prefix.lastIndexOf('_')
  if (underscoreIdx >= 0) {
    const potentialQuote = prefix.slice(underscoreIdx + 1).toUpperCase()
    // Accept stablecoins, or any quote when there's no hyphen (pure spot symbol like ETH_BTC)
    if (STABLECOINS.includes(potentialQuote) || hyphenIdx < 0) {
      return {
        baseTicker: prefix.slice(0, underscoreIdx),
        quoteCurrency: potentialQuote,
      }
    }
  }

  // Default: USD-quoted (covers BTC-PERPETUAL, BTC-FS-..., BTC-27FEB26, etc.)
  return { baseTicker: prefix, quoteCurrency: 'USD' }
}

export function getInstrumentBaseCurrency(symbol: string): string {
  return parseInstrumentSymbol(symbol).baseTicker.toUpperCase()
}

export function getTickerBaseCurrency(ticker: string): string {
  const underscoreIdx = ticker.lastIndexOf('_')
  if (underscoreIdx >= 0) {
    return ticker.slice(0, underscoreIdx).toUpperCase()
  }

  return ticker.toUpperCase()
}

/**
 * Extract a human-readable expiry/date label from a Deribit instrument symbol.
 * Returns null for perpetuals, spots, and unknown formats.
 *
 * Examples:
 *   BTC-27FEB26                  → '27FEB26'
 *   BTC_USDC-27FEB26             → '27FEB26'
 *   BTC-FS-25SEP26_27FEB26       → '25SEP26 / 27FEB26'
 *   BTC-FS-25SEP26_PERP          → '25SEP26 / Perp'
 *   BTC-PERPETUAL                → null
 *   BTC-USD  (spot)              → null
 */

const DATE_RE = /^\d{1,2}[A-Z]{3}\d{2}$/

export function parseSymbolExpiry(symbol: string): string | null {
  const hyphenIdx = symbol.indexOf('-')
  if (hyphenIdx < 0) return null

  const contractPart = symbol.slice(hyphenIdx + 1)

  // Perpetual — no expiry to show
  if (contractPart === 'PERPETUAL' || contractPart === 'PERP') return null

  // Calendar spread: FS-25SEP26_27FEB26  or  FS-25SEP26_PERP
  if (contractPart.startsWith('FS-')) {
    const datesStr = contractPart.slice(3) // '25SEP26_27FEB26'
    const underIdx = datesStr.indexOf('_')
    if (underIdx < 0) return null
    const start = datesStr.slice(0, underIdx)
    const end = datesStr.slice(underIdx + 1)
    if (!DATE_RE.test(start)) return null
    if (end === 'PERP' || end === 'PERPETUAL') return `${start} / Perp`
    if (DATE_RE.test(end)) return `${start} / ${end}`
    return null
  }

  // Simple future: 27FEB26
  if (DATE_RE.test(contractPart)) return contractPart

  return null
}
